import { useState, useRef, useCallback } from 'react';

import { getFeatureDisabledHandler } from '@/features/feature-flags';

import { streamDsp } from '../utils/dsp-stream';

import type { DspParserState, MessagePart } from '../utils/dsp-parser';
import type { ThreadMessage, ThreadAssistantMessage } from '@assistant-ui/react';

export interface UseStreamingMessageReturn {
  message: ThreadMessage | null;
  messageId: string | null;
  isStreaming: boolean;
  error: string | null;
  startStream: (streamUrl: string) => Promise<void>;
  stopStream: () => void;
  resetStream: () => void;
}

export const useStreamingMessage = (): UseStreamingMessageReturn => {
  const [message, setMessage] = useState<ThreadMessage | null>(null);
  const [messageId, setMessageId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Only abortRef remains — it controls the generator's AbortSignal.
  // No partsRef or messageIdRef — the generator owns accumulation state (STR-03).
  const abortRef = useRef<AbortController | null>(null);
  const previousErrorRef = useRef<string | null>(null);

  const buildMessage = useCallback(
    (parts: MessagePart[], msgId: string | null, isComplete: boolean): ThreadMessage => {
      return {
        id: msgId ?? 'streaming',
        role: 'assistant' as const,
        content: [...parts] as unknown as ThreadAssistantMessage['content'],
        createdAt: new Date(),
        status: isComplete
          ? { type: 'complete' as const, reason: 'unknown' as const }
          : { type: 'running' as const },
        metadata: {
          unstable_state: null,
          unstable_annotations: [] as readonly never[],
          unstable_data: [] as readonly never[],
          steps: [] as readonly never[],
          custom: {},
        },
      } as ThreadAssistantMessage as ThreadMessage;
    },
    [],
  );

  const startStream = useCallback(
    async (streamUrl: string) => {
      const controller = new AbortController();
      abortRef.current = controller;

      // Reset state
      setMessage(null);
      setMessageId(null);
      setError(null);
      setIsStreaming(true);

      try {
        let lastState: DspParserState | null = null;
        for await (const state of streamDsp(streamUrl, controller.signal)) {
          lastState = state;
          if (state.error) {
            setError(state.error);
            if (
              state.error === '[FEATURE_DISABLED]' &&
              previousErrorRef.current !== '[FEATURE_DISABLED]'
            ) {
              getFeatureDisabledHandler()?.();
            }
          }
          previousErrorRef.current = state.error;
          if (state.parts.length > 0) {
            setMessageId(state.messageId);
            setMessage(buildMessage(state.parts, state.messageId, false));
          }
        }

        // Final message with complete status
        if (lastState && lastState.parts.length > 0) {
          setMessage(buildMessage(lastState.parts, lastState.messageId, true));
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          const msg = (err as Error).message;
          // Generator throws specific message for HTTP errors; network failures get generic message
          setError(msg === 'Failed to connect to streaming service' ? msg : 'Connection lost');
        }
      } finally {
        setIsStreaming(false);
      }
    },
    [buildMessage],
  );

  const stopStream = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const resetStream = useCallback(() => {
    setMessage(null);
    setMessageId(null);
    setError(null);
    setIsStreaming(false);
  }, []);

  return { message, messageId, isStreaming, error, startStream, stopStream, resetStream };
};
